import React from 'react';
import { FaThumbtack } from 'react-icons/fa';
import { Message } from '../../types';
import AvatarImage from '../common/AvatarImage';

interface PinnedMessageItemProps {
  message: Message;
  onUnpin?: (messageId: string) => void;
  onClick?: (messageId: string) => void;
}

const PinnedMessageItem: React.FC<PinnedMessageItemProps> = ({ message, onUnpin, onClick }) => {
  const formatDate = (date: Date | string) => {
    const d = new Date(date);
    return d.toLocaleDateString(undefined, {
      month: 'short',
      day: 'numeric',
      year: d.getFullYear() !== new Date().getFullYear() ? 'numeric' : undefined,
    });
  };

  const formatTime = (date: Date | string) => {
    const d = new Date(date);
    return d.toLocaleTimeString(undefined, {
      hour: '2-digit',
      minute: '2-digit',
    });
  };

  return (
    <div
      onClick={() => onClick?.(message.id)}
      className="group flex gap-3 p-3 bg-gradient-to-r from-gray-50 to-gray-100 dark:from-gray-700/50 dark:to-gray-700/30 hover:from-primary-50 hover:to-primary-100 dark:hover:from-primary-900/20 dark:hover:to-primary-800/10 rounded-lg transition-all duration-200 border border-brand-outline-variant dark:border-brand-outline hover:border-primary-300 dark:hover:border-primary-700/50 hover:shadow-md cursor-pointer"
    >
      {/* Avatar */}
      <div className="flex-shrink-0 w-8 h-8 rounded-full overflow-hidden ring-2 ring-gray-200 dark:ring-gray-600 group-hover:ring-primary-400 dark:group-hover:ring-primary-600 transition-all duration-200">
        <AvatarImage
          src={message.sender?.avatarUrl}
          alt={message.sender?.username || 'User'}
          className="w-8 h-8 rounded-full object-cover"
          fallback={
            <div className="w-8 h-8 rounded-full bg-gradient-to-br from-primary-400 to-primary-600 flex items-center justify-center text-brand-on-primary text-xs font-bold">
              {message.sender?.username?.charAt(0).toUpperCase() || 'U'}
            </div>
          }
        />
      </div>

      {/* Content */}
      <div className="flex-1 min-w-0">
        <div className="flex items-baseline justify-between gap-2 mb-1">
          <p className="font-semibold text-brand-on-background dark:text-brand-on-primary text-sm truncate group-hover:text-primary-600 dark:group-hover:text-primary-400 transition-colors duration-200">
            {message.sender?.username || 'Unknown User'}
          </p>
          <p className="text-xs text-brand-outline dark:text-brand-on-surface-variant group-hover:text-brand-on-surface-variant dark:group-hover:text-brand-on-surface-variant flex-shrink-0 transition-colors duration-200 whitespace-nowrap">
            {formatDate(message.createdAt)} at {formatTime(message.createdAt)}
          </p>
        </div>
        <p className="text-sm text-brand-on-surface dark:text-brand-on-surface-variant line-clamp-3 break-words leading-relaxed group-hover:text-brand-on-background dark:group-hover:text-brand-on-background transition-colors duration-200 whitespace-pre-wrap">
          {message.content}
        </p>
        {message.attachments && message.attachments.length > 0 && (
          <div className="mt-2 inline-flex items-center gap-1 px-2 py-1 bg-brand-surface dark:bg-brand-surface-variant rounded text-xs text-brand-on-surface-variant dark:text-brand-on-surface-variant group-hover:bg-primary-200 dark:group-hover:bg-primary-700/50 group-hover:text-primary-700 dark:group-hover:text-primary-300 transition-all duration-200">
            <span>📎</span>
            <span>
              {message.attachments.length} attachment{message.attachments.length !== 1 ? 's' : ''}
            </span>
          </div>
        )}
      </div>

      {/* Unpin button */}
      {onUnpin && (
        <button
          onClick={(e) => {
            e.stopPropagation();
            onUnpin(message.id);
          }}
          className="flex-shrink-0 p-2 text-brand-on-surface-variant dark:text-brand-outline hover:text-brand-error dark:hover:text-brand-error hover:bg-red-100 dark:hover:bg-red-900/30 rounded-lg transition-all duration-200 opacity-0 group-hover:opacity-100 focus-visible:opacity-100 transform group-hover:scale-110 focus-visible:ring-2 focus-visible:ring-primary-500 focus:outline-none"
          title="Unpin message"
          aria-label="Unpin message"
        >
          <FaThumbtack size={14} className="rotate-45" />
        </button>
      )}
    </div>
  );
};

export default PinnedMessageItem;
